import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const COLLAPSED_LIMIT = 14;

interface TermChipsProps {
  tickers?: string[];
  keywords?: string[];
  /** Omit to render read-only chips (library lists can't be edited here). */
  onRemoveTicker?: (ticker: string) => void;
  onRemoveKeyword?: (keyword: string) => void;
}

/**
 * The contents of a watchlist, as chips. Long lists collapse to a readable
 * first row with a "+N more" toggle — a 444-ticker index list should not
 * bury the rest of the plan.
 */
export default function TermChips({
  tickers = [],
  keywords = [],
  onRemoveTicker,
  onRemoveKeyword,
}: TermChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const total = tickers.length + keywords.length;
  const overflowing = total > COLLAPSED_LIMIT && !expanded;

  let budget = overflowing ? COLLAPSED_LIMIT : Infinity;
  const shownTickers = tickers.slice(0, Math.max(0, budget));
  budget -= shownTickers.length;
  const shownKeywords = keywords.slice(0, Math.max(0, budget));
  const hidden = total - shownTickers.length - shownKeywords.length;

  const base =
    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors";

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {shownTickers.map((ticker) => (
        <span
          key={ticker}
          className={`${base} border border-gray-700 bg-gray-800 font-mono text-gray-300 hover:bg-gray-700`}
        >
          {ticker}
          {onRemoveTicker && (
            <button
              type="button"
              onClick={() => onRemoveTicker(ticker)}
              aria-label={`Remove ${ticker}`}
              className="cursor-pointer border-none bg-transparent p-0 text-gray-600 transition-colors hover:text-red-400"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {shownKeywords.map((keyword) => (
        <span
          key={keyword}
          className={`${base} bg-squawk-blue/[0.1] text-squawk-blue hover:bg-squawk-blue/20`}
        >
          {keyword}
          {onRemoveKeyword && (
            <button
              type="button"
              onClick={() => onRemoveKeyword(keyword)}
              aria-label={`Remove ${keyword}`}
              className="cursor-pointer border-none bg-transparent p-0 text-squawk-blue/50 transition-colors hover:text-red-400"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {(overflowing || expanded) && total > COLLAPSED_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="cursor-pointer rounded-md border-none bg-transparent px-1.5 py-0.5 text-[11px] font-semibold text-gray-500 transition-colors hover:text-squawk-blue"
        >
          {expanded ? "Show less" : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}
